import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router-dom";
// Direct module import (not the ../identity barrel) to avoid an import cycle:
// identity/hooks pulls useQuery from this package's barrel.
import { workspaceKeyPrefix } from "../identity/active";
import { DAY_MS, HOUR_MS, dashboardNow, dateOnly, isoSecond } from "./clock";
import type { AnalyticsQuery, OrgStructure } from "./types";

export type RangePreset = "1h" | "1d" | "7d" | "30d" | "custom";

export type Scope =
  | { kind: "org" }
  | { kind: "team"; id: string }
  | { kind: "repo"; name: string };

export interface TimeRange {
  preset: RangePreset;
  /** Resolved inclusive bounds as date-only UTC strings (YYYY-MM-DD). */
  from: string;
  to: string;
}

export interface FiltersContextValue {
  org: OrgStructure;

  scope: Scope;
  scopeLabel: string;
  setScope: (scope: Scope) => void;

  range: TimeRange;
  setPreset: (preset: Exclude<RangePreset, "custom">) => void;
  setCustomRange: (from: string, to: string) => void;

  models: string[];
  setModels: (models: string[]) => void;
  /** Agent slices the dashboard is narrowed to (`x-agent-id`), from `?agents=`. */
  agentIds: string[];
  setAgentIds: (agentIds: string[]) => void;
  configs: string[];
  setConfigs: (configs: string[]) => void;

  routed: boolean | undefined;
  setRouted: (routed: boolean | undefined) => void;

  /** Filters beyond scope+time (model, agent, config, routed). */
  activeFilterCount: number;
  resetFilters: () => void;

  /** The query object to hand to the analytics client. */
  query: AnalyticsQuery;

  /** Force a refetch of every mounted analytics hook (drives auto-refresh). */
  bumpRefresh: () => void;
  /** Epoch ms of the last refresh bump, or null if never (drives the cue). */
  lastRefreshed: number | null;
}

const FiltersContext = createContext<FiltersContextValue | null>(null);

/** Each preset's window length, in milliseconds. `7d`/`30d` keep date-only bounds
 *  (whole UTC days); `1h` and `1d` carry a time-of-day so the window is an exact
 *  rolling span (see presetRange). */
const PRESET_MS: Record<Exclude<RangePreset, "custom">, number> = {
  "1h": HOUR_MS,
  "1d": DAY_MS,
  "7d": 7 * DAY_MS,
  "30d": 30 * DAY_MS,
};

function isPreset(raw: string): raw is Exclude<RangePreset, "custom"> {
  return Object.prototype.hasOwnProperty.call(PRESET_MS, raw);
}

export function presetRange(
  preset: Exclude<RangePreset, "custom">,
  // Injectable for tests; defaults to the live/mock reference clock. Re-read per
  // call so a refresh rolls the window forward.
  now: Date = dashboardNow(),
): TimeRange {
  const to = now;
  // `1d` is a TRUE rolling last-24h ("Last 24 hours" must mean that, not the two
  // whole UTC days a date-only bound expands to). Its START is snapped DOWN to the
  // hour so the request stays on the gateway's hourly rollup read path: a sub-hour
  // start is not hour-aligned, so the BFF falls back to the live byte-derived path
  // which can't credit pxpipe savings (they'd read $0). `to` stays live (now).
  if (preset === "1d") {
    const from = new Date(to.getTime() - DAY_MS);
    from.setUTCMinutes(0, 0, 0);
    return { preset, from: isoSecond(from), to: isoSecond(to) };
  }

  const from = new Date(to.getTime() - PRESET_MS[preset]);
  // `1h` carries a time-of-day so the window is the exact rolling span; `7d`/`30d`
  // round to whole UTC days. The gateway reads a datetime bound as-is (exact live
  // query) and a date-only bound as a full day.
  const bound = PRESET_MS[preset] < DAY_MS ? isoSecond : dateOnly;
  return { preset, from: bound(from), to: bound(to) };
}

function parseScope(raw: string | null, org: OrgStructure): Scope {
  if (!raw || raw === "org") return { kind: "org" };

  const [kind, id] = raw.split(":");
  if (kind === "team" && org.teams.some((t) => t.id === id)) {
    return { kind: "team", id };
  }

  if (kind === "repo" && org.teams.some((t) => t.repos.includes(id))) {
    return { kind: "repo", name: id };
  }

  return { kind: "org" };
}

function serializeScope(scope: Scope): string {
  if (scope.kind === "org") return "org";

  if (scope.kind === "team") return `team:${scope.id}`;

  return `repo:${scope.name}`;
}

function parseRange(raw: string | null): TimeRange {
  if (raw && raw.includes("..")) {
    const [from, to] = raw.split("..");
    if (from && to) return { preset: "custom", from, to };
  }

  if (raw && raw !== "7d" && isPreset(raw)) return presetRange(raw);

  // Default to 7 days: the gateway now serves wide windows (up to 92 days) for
  // the aggregate panels with sub-second latency, so the first view shows a
  // useful week. Narrower/wider presets stay one click away.
  return presetRange("7d");
}

function csv(raw: string | null): string[] {
  return raw ? raw.split(",").filter(Boolean) : [];
}

function reposForScope(scope: Scope, org: OrgStructure): string[] | undefined {
  if (scope.kind === "org") return undefined;

  if (scope.kind === "repo") return [scope.name];

  return org.teams.find((t) => t.id === scope.id)?.repos ?? [];
}

function scopeLabelFor(scope: Scope, org: OrgStructure): string {
  if (scope.kind === "org") return `${org.org} · all repos`;

  if (scope.kind === "repo") return scope.name;

  return org.teams.find((t) => t.id === scope.id)?.name ?? scope.id;
}

export interface FiltersProviderProps {
  org: OrgStructure;
  children: ReactNode;
}

export function FiltersProvider({ org, children }: FiltersProviderProps) {
  const [params, setParams] = useSearchParams();

  // Refresh generation: bumping it re-keys every analytics query (via
  // queryKey's suffix) so mounted hooks refetch. lastRefreshed drives the cue.
  const [refreshTick, setRefreshTick] = useState(0);
  const [lastRefreshed, setLastRefreshed] = useState<number | null>(null);
  // Mirror the tick into the module generation queryKey() reads, synchronously
  // during render so children computing keys this same render see the new value
  // (the hooks call queryKey() below us in the tree). It's an idempotent mirror
  // of state, not derived data — safe to write in render.
  setRefreshGeneration(refreshTick);
  const bumpRefresh = useCallback(() => {
    setRefreshTick((t) => t + 1);
    setLastRefreshed(Date.now());
  }, []);

  const scope = useMemo(
    () => parseScope(params.get("scope"), org),
    [params, org],
  );
  const range = useMemo(
    () => parseRange(params.get("range")),
    // refreshTick re-resolves presets against a fresh dashboardNow() on refresh;
    // the linter can't see that dep since it's read outside React.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [params, refreshTick],
  );
  const models = useMemo(() => csv(params.get("models")), [params]);
  const agentIds = useMemo(() => csv(params.get("agents")), [params]);
  const configs = useMemo(() => csv(params.get("configs")), [params]);
  const routedParam = params.get("routed");
  const routed = routedParam === null ? undefined : routedParam === "true";

  const update = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const setScope = useCallback(
    (s: Scope) =>
      update((next) => {
        if (s.kind === "org") next.delete("scope");
        else next.set("scope", serializeScope(s));
      }),
    [update],
  );

  const setPreset = useCallback(
    (preset: Exclude<RangePreset, "custom">) =>
      update((next) => {
        if (preset === "7d") next.delete("range");
        else next.set("range", preset);
      }),
    [update],
  );

  const setCustomRange = useCallback(
    (from: string, to: string) =>
      update((next) => next.set("range", `${from}..${to}`)),
    [update],
  );

  const setListParam = useCallback(
    (name: string, values: string[]) =>
      update((next) => {
        if (values.length) next.set(name, values.join(","));
        else next.delete(name);
      }),
    [update],
  );

  const setRouted = useCallback(
    (value: boolean | undefined) =>
      update((next) => {
        if (value === undefined) next.delete("routed");
        else next.set("routed", String(value));
      }),
    [update],
  );

  const resetFilters = useCallback(
    () =>
      update((next) => {
        next.delete("models");
        next.delete("agents");
        next.delete("configs");
        next.delete("routed");
      }),
    [update],
  );

  const query: AnalyticsQuery = useMemo(() => {
    const q: AnalyticsQuery = { from: range.from, to: range.to };
    const repos = reposForScope(scope, org);
    if (repos) q.repo = repos;

    if (models.length) q.model = models;

    if (agentIds.length) q.agent_id = agentIds;

    if (configs.length) q.config_id = configs;

    if (routed !== undefined) q.routed = routed;

    return q;
  }, [range.from, range.to, scope, org, models, agentIds, configs, routed]);

  const value: FiltersContextValue = {
    org,
    scope,
    scopeLabel: scopeLabelFor(scope, org),
    setScope,
    range,
    setPreset,
    setCustomRange,
    models,
    setModels: (m) => setListParam("models", m),
    agentIds,
    setAgentIds: (a) => setListParam("agents", a),
    configs,
    setConfigs: (c) => setListParam("configs", c),
    routed,
    setRouted,
    activeFilterCount:
      models.length +
      agentIds.length +
      configs.length +
      (routed !== undefined ? 1 : 0),
    resetFilters,
    query,
    bumpRefresh,
    lastRefreshed,
  };

  return (
    <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>
  );
}

export function useFilters(): FiltersContextValue {
  const ctx = useContext(FiltersContext);
  if (!ctx) throw new Error("useFilters must be used within a FiltersProvider");

  return ctx;
}

/* A process-wide "refresh generation" folded into every analytics query key.
 * Bumping it (via the FiltersProvider's auto-refresh) changes all keys at once,
 * so every mounted analytics hook refetches — the lever the polling feature
 * pulls. Module-scoped because queryKey() is a free function; there is exactly
 * one FiltersProvider driving it. */
let refreshGeneration = 0;
export function setRefreshGeneration(gen: number): void {
  refreshGeneration = gen;
}

/** A stable string key for the active query — handy for useQuery cache keys.
 *  Prefixed with the active workspace so a workspace switch changes every key
 *  (no stale cross-workspace data; switching back re-serves its cache), and
 *  suffixed with the refresh generation so a poll tick refetches everything. */
export function queryKey(query: AnalyticsQuery, extra?: string): string {
  return (
    workspaceKeyPrefix() +
    JSON.stringify(query) +
    (extra ? `|${extra}` : "") +
    (refreshGeneration ? `|r${refreshGeneration}` : "")
  );
}
